import { motion } from 'framer-motion';
import { Minus, Plus, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const CartPage = () => {
  const { t, language } = useLanguage();
  const { cartItems, updateQuantity, removeFromCart, cartTotal } = useCart();

  const formatPrice = (price) => {
    return language === 'fr' 
      ? `${price.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`
      : `€${price.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const handleCheckout = () => {
    toast.info(language === 'fr' ? 'Fonctionnalité de paiement non disponible' : 'Checkout feature not available');
  };

  return (
    <main className="min-h-screen pt-28 pb-16 bg-white" data-testid="cart-page">
      <div className="container-custom">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="heading-xl text-4xl md:text-5xl mb-12 text-black"
          data-testid="cart-title"
        >
          {t('cart.title')}
        </motion.h1>

        {cartItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center py-20 bg-neutral-50 rounded-2xl"
            data-testid="cart-empty"
          >
            <p className="text-neutral-500 text-lg font-sans">
              {t('cart.empty')}
            </p>
          </motion.div>
        ) : (
          <div className="max-w-3xl">
            {/* Cart Items */}
            <div className="space-y-4 mb-8" data-testid="cart-items">
              {cartItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.01 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center gap-4 md:gap-6 p-6 bg-white rounded-2xl border border-neutral-100 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`cart-item-${index}`}
                >
                  {/* Image */}
                  <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 overflow-hidden rounded-xl">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      data-testid={`cart-item-image-${index}`}
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h3 
                      className="font-serif text-lg md:text-xl font-semibold truncate text-black"
                      data-testid={`cart-item-name-${index}`}
                    >
                      {item.name}
                    </h3>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3 mt-3">
                      <motion.button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="w-8 h-8 flex items-center justify-center border border-neutral-200 rounded-full hover:border-black transition-colors"
                        data-testid={`cart-item-decrease-${index}`}
                      >
                        <Minus size={14} />
                      </motion.button>
                      <span 
                        className="font-sans text-base w-8 text-center"
                        data-testid={`cart-item-quantity-${index}`}
                      >
                        {item.quantity}
                      </span>
                      <motion.button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="w-8 h-8 flex items-center justify-center border border-neutral-200 rounded-full hover:border-black transition-colors"
                        data-testid={`cart-item-increase-${index}`}
                      >
                        <Plus size={14} />
                      </motion.button>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    <p 
                      className="font-sans text-lg font-semibold text-black"
                      data-testid={`cart-item-price-${index}`}
                    >
                      {formatPrice(item.price * item.quantity)}
                    </p>
                  </div>

                  {/* Remove Button */}
                  <motion.button
                    onClick={() => removeFromCart(item.id)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                    data-testid={`cart-item-remove-${index}`}
                  >
                    <X size={20} />
                  </motion.button>
                </motion.div>
              ))}
            </div>

            {/* Subtotal */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex justify-between items-center py-6 border-t border-b border-neutral-200"
              data-testid="cart-subtotal"
            >
              <span className="font-sans text-lg text-black">{t('cart.subtotal')}</span>
              <span 
                className="font-serif text-2xl font-bold text-black"
                data-testid="cart-total-amount"
              >
                {formatPrice(cartTotal)}
              </span>
            </motion.div>

            {/* Checkout Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              onClick={handleCheckout}
              className="btn-pill btn-primary w-full mt-8 py-5 text-lg"
              data-testid="checkout-button"
            >
              {t('cart.checkout')}
            </motion.button>
          </div>
        )}
      </div>
    </main>
  );
};

export default CartPage;
